/**
 * Subscription tier entitlements (mirrors functions/utils/entitlements.js).
 * Keep numbers and Pro feature keys identical on both sides.
 */

/** @type {Record<string, number|null>} null = unlimited */
export const ACTIVE_CLIENT_LIMITS = {
  free: 5,
  standard: 30,
  pro: 100,
  special_pricing: null,
};

/** Features that require Pro (or special_pricing). */
export const PRO_FEATURES = [
  "ai_features",
  "training_plan_imports",
  "ai_progress_photo_analysis",
  "ai_insights",
  "training_plan_data_entry_assistance",
  "meal_plan_data_entry_assistance",
  "priority_support",
  "api_access",
  "custom_integrations",
];

/** Core coaching features available on Free and Standard. */
export const CORE_FEATURES = [
  "basic_dashboard",
  "client_management",
  "coach_management",
  "basic_reports",
  "custom_training_plans",
  "text_messaging",
  "meal_plans",
  "custom_branding",
  "audio_uploads",
  "image_uploads",
  "video_uploads",
  "file_sharing",
  "advanced_reports",
  "analytics",
  "custom_protocols",
  "progress_tracking",
  "help_ai_chat",
];

/**
 * Normalize organization accountType to a subscription tier.
 * @param {Object} organizationData
 * @returns {string} "free" | "standard" | "pro" | "special_pricing"
 */
export const getSubscriptionTier = (organizationData) => {
  if (!organizationData || !organizationData.account) {
    return "free";
  }

  const accountType = organizationData.account.accountType;

  if (accountType === "trial" || accountType === null || accountType === undefined) {
    return "free";
  }

  if (accountType === "stripe") {
    return "standard";
  }

  if (
    accountType === "free" ||
    accountType === "standard" ||
    accountType === "pro" ||
    accountType === "special_pricing"
  ) {
    return accountType;
  }

  return "free";
};

/**
 * Normalize a raw accountType string (no org object).
 * @param {string|null|undefined} accountType
 * @returns {string}
 */
export const normalizeAccountType = (accountType) => {
  return getSubscriptionTier({ account: { accountType } });
};

/**
 * Max active clients for a tier. null = unlimited.
 * @param {string} tier
 * @returns {number|null}
 */
export const getActiveClientLimit = (tier) => {
  const normalized = normalizeAccountType(tier);
  if (Object.prototype.hasOwnProperty.call(ACTIVE_CLIENT_LIMITS, normalized)) {
    return ACTIVE_CLIENT_LIMITS[normalized];
  }
  return ACTIVE_CLIENT_LIMITS.free;
};

/**
 * @param {number} currentActiveCount
 * @param {string|Object} tierOrOrg - tier string or organizationData
 * @returns {boolean}
 */
export const canAddActiveClient = (currentActiveCount, tierOrOrg) => {
  const resolvedTier =
    typeof tierOrOrg === "object" && tierOrOrg !== null
      ? getSubscriptionTier(tierOrOrg)
      : normalizeAccountType(tierOrOrg);
  const resolvedLimit = getActiveClientLimit(resolvedTier);
  if (resolvedLimit === null || resolvedLimit === undefined) {
    return true;
  }
  return (currentActiveCount || 0) < resolvedLimit;
};

/**
 * Check if organization has access to a specific feature.
 * @param {Object} organizationData
 * @param {string} featureName
 * @returns {boolean}
 */
export const hasFeatureAccess = (organizationData, featureName) => {
  const tier = getSubscriptionTier(organizationData);

  if (CORE_FEATURES.includes(featureName)) {
    return true;
  }

  if (PRO_FEATURES.includes(featureName)) {
    return tier === "pro" || tier === "special_pricing";
  }

  return false;
};

/**
 * @param {Object} organizationData
 * @param {string} minTier - "free" | "standard" | "pro"
 * @returns {boolean}
 */
export const isTierAtLeast = (organizationData, minTier) => {
  const tier = getSubscriptionTier(organizationData);

  const tierLevels = {
    free: 0,
    standard: 1,
    pro: 2,
    special_pricing: 3,
  };

  const currentLevel = tierLevels[tier] || 0;
  const minLevel = tierLevels[minTier] || 0;

  return currentLevel >= minLevel;
};

/**
 * @param {string} tier
 * @returns {string}
 */
export const getTierDisplayName = (tier) => {
  const names = {
    free: "Free",
    standard: "Standard",
    pro: "Pro",
    special_pricing: "Special Pricing",
    trial: "Free Trial",
    stripe: "Standard",
  };

  return names[tier] || "Free";
};

/**
 * @param {string} tier
 * @returns {number|null}
 */
export const getTierPrice = (tier) => {
  const prices = {
    free: 0,
    standard: 49.95,
    pro: 99.95,
    special_pricing: null,
  };

  return prices[tier] !== undefined ? prices[tier] : null;
};
